/**
 * User Profile page — manages licences, hours, personal details, and preferences.
 * Route: #/profile
 * Requirements: 16.1, 16.2, 16.3, 16.4, 16.5, 16.6, 16.7
 */

import { getUser, getRole } from '../services/auth.js';
import {
  getPilotLicences,
  createPilotLicence,
  updatePilotLicence,
  deletePilotLicence,
  updatePilot,
} from '../services/api.js';
import { getSupabase } from '../services/supabase.js';
import { navigate } from '../services/router.js';
import { formatDateUK } from '../utils/format.js';
import { TIME_FORMATS } from '../utils/constants.js';
import { validateProfileUpdate, validatePasswordChange } from '../utils/validation.js';
import { openDetailModal } from '../components/detail-modal.js';
import { createLicenceCard, isLicenceExpired } from '../components/licence-card.js';

// ---------------------------------------------------------------------------
// Pure helper functions (exported for testability)
// ---------------------------------------------------------------------------

/**
 * Format a licence object for display, including an expiry warning flag.
 *
 * @param {{ licence_type: string, rating_category: string, expiry_date: string }} licence
 * @param {Date} [referenceDate] - defaults to now
 * @returns {{ type: string, rating: string, expiryFormatted: string, expired: boolean, expiringSoon: boolean }}
 */
export function formatLicenceForDisplay(licence, referenceDate) {
  if (!licence) return { type: '—', rating: '—', expiryFormatted: '—', expired: false, expiringSoon: false };

  const ref = referenceDate || new Date();
  const expired = isLicenceExpired(licence, ref);

  let expiringSoon = false;
  if (!expired && licence.expiry_date) {
    const expiry = new Date(licence.expiry_date);
    const thirtyDays = new Date(ref);
    thirtyDays.setDate(thirtyDays.getDate() + 30);
    expiringSoon = expiry <= thirtyDays;
  }

  return {
    type: licence.licence_type || '—',
    rating: licence.rating_category || '—',
    expiryFormatted: licence.expiry_date ? formatDateUK(licence.expiry_date) : '—',
    expired,
    expiringSoon,
  };
}

/**
 * Check if a licence is expired relative to a reference date.
 * Re-exported from licence-card for convenience; delegates to the component.
 *
 * @param {{ expiry_date: string }} licence
 * @param {Date} [referenceDate]
 * @returns {boolean}
 */
export { isLicenceExpired } from '../components/licence-card.js';

/**
 * Prepare a licence payload from form data.
 *
 * @param {{ licence_type: string, rating_category: string, expiry_date: string }} formData
 * @returns {{ licence_type: string, rating_category: string, expiry_date: string }}
 */
export function prepareLicencePayload(formData) {
  return {
    licence_type: (formData.licence_type || '').trim(),
    rating_category: (formData.rating_category || '').trim(),
    expiry_date: (formData.expiry_date || '').trim(),
  };
}

/**
 * Format hours for display.
 *
 * @param {number|null|undefined} totalHours
 * @param {number|null|undefined} p1Hours
 * @returns {{ total: string, p1: string }}
 */
export function formatHoursForDisplay(totalHours, p1Hours) {
  const fmt = (v) => {
    if (v == null || v === '' || Number.isNaN(Number(v))) return '0.0';
    return Number(v).toFixed(1);
  };
  return {
    total: fmt(totalHours),
    p1: fmt(p1Hours),
  };
}

// ---------------------------------------------------------------------------
// Page render
// ---------------------------------------------------------------------------

export async function openProfileModal() {
  const user = getUser();
  if (!user) return;

  // Remove any existing modal
  document.getElementById('profile-modal-overlay')?.remove();

  const overlay = document.createElement('div');
  overlay.id = 'profile-modal-overlay';
  overlay.className = 'profile-modal-overlay';
  overlay.innerHTML = `
    <div class="profile-modal" role="dialog" aria-modal="true" aria-label="Profile">
      <div class="profile-modal__header">
        <h2>Profile</h2>
        <button class="profile-modal__close" id="profile-modal-close" title="Close">✕</button>
      </div>
      <div class="profile-modal__body">
        <div id="profile-content" class="profile-content"><p>Loading…</p></div>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);

  overlay.addEventListener('click', (e) => { if (e.target === overlay) overlay.remove(); });
  overlay.querySelector('#profile-modal-close').addEventListener('click', () => overlay.remove());

  // Trap Escape key
  const onKey = (e) => { if (e.key === 'Escape') { overlay.remove(); document.removeEventListener('keydown', onKey); } };
  document.addEventListener('keydown', onKey);

  try {
    await loadProfileContent(user.id);
  } catch (err) {
    const el = document.getElementById('profile-content');
    if (el) el.innerHTML = `<p class="error">Failed to load profile: ${err.message}</p>`;
  }
}

export async function renderProfile() {
  const app = document.getElementById('app');
  if (!app) return;

  const user = getUser();
  if (!user) { navigate('/login'); return; }

  app.innerHTML = `
    <div class="container">
      <h2>User Profile</h2>
      <div id="profile-content" class="profile-content">
        <p>Loading…</p>
      </div>
    </div>
  `;

  try {
    await loadProfileContent(user.id);
  } catch (err) {
    document.getElementById('profile-content').innerHTML =
      `<p class="error">Failed to load profile: ${err.message}</p>`;
  }
}

async function loadProfileContent(pilotId) {
  const container = document.getElementById('profile-content');
  if (!container) return;

  // Fetch pilot data and licences in parallel
  const supabase = getSupabase();
  const [pilotRes, licences] = await Promise.all([
    supabase.from('pilots').select('*').eq('id', pilotId).single(),
    getPilotLicences(pilotId),
  ]);
  if (pilotRes.error) throw pilotRes.error;
  const pilot = pilotRes.data;

  container.innerHTML = '';

  // ── Licences section ──────────────────────────────────────────────────
  const licSection = document.createElement('section');
  licSection.className = 'profile-section';
  licSection.innerHTML = `
    <div class="profile-section__header">
      <h2>Licences</h2>
      <button class="btn btn--primary" id="add-licence-btn">Add Licence</button>
    </div>
    <div id="licence-list" class="licence-list"></div>
  `;
  container.appendChild(licSection);
  renderLicenceList(licences, pilotId);

  licSection.querySelector('#add-licence-btn').addEventListener('click', () => {
    openLicenceModal(null, pilotId);
  });

  // ── Hours section ─────────────────────────────────────────────────────
  const hoursSection = document.createElement('section');
  hoursSection.className = 'profile-section';
  const hrs = formatHoursForDisplay(pilot.total_hours, pilot.p1_hours);
  hoursSection.innerHTML = `
    <h2>Flying Hours</h2>
    <form id="hours-form" class="profile-form">
      <div class="form-group">
        <label for="total-hours">Total Hours</label>
        <input type="number" id="total-hours" name="total_hours" step="0.1" min="0" value="${hrs.total}" />
      </div>
      <div class="form-group">
        <label for="p1-hours">P1 Hours</label>
        <input type="number" id="p1-hours" name="p1_hours" step="0.1" min="0" value="${hrs.p1}" />
      </div>
      <button type="submit" class="btn btn--primary">Update Hours</button>
      <span id="hours-msg" class="form-msg"></span>
    </form>
  `;
  container.appendChild(hoursSection);

  hoursSection.querySelector('#hours-form').addEventListener('submit', async (e) => {
    e.preventDefault();
    const msgEl = hoursSection.querySelector('#hours-msg');
    try {
      const totalVal = parseFloat(hoursSection.querySelector('#total-hours').value) || 0;
      const p1Val = parseFloat(hoursSection.querySelector('#p1-hours').value) || 0;
      await updatePilot(pilotId, { total_hours: totalVal, p1_hours: p1Val });
      msgEl.textContent = 'Hours updated.';
      msgEl.className = 'form-msg form-msg--success';
    } catch (err) {
      msgEl.textContent = err.message || 'Update failed.';
      msgEl.className = 'form-msg form-msg--error';
    }
  });

  // ── Personal details section ──────────────────────────────────────────
  const detailsSection = document.createElement('section');
  detailsSection.className = 'profile-section';
  const timeFormat = pilot.meta?.time_format || TIME_FORMATS.HMMSS;
  detailsSection.innerHTML = `
    <h2>Personal Details</h2>
    <form id="details-form" class="profile-form">
      <div class="form-group">
        <label for="profile-name">Name</label>
        <input type="text" id="profile-name" name="name" value="${pilot.name || ''}" required />
      </div>
      <div class="form-group">
        <label for="profile-email">Email</label>
        <input type="email" id="profile-email" name="email" value="${pilot.email || ''}" required />
      </div>
      <div class="form-group">
        <label for="time-format">Time Format</label>
        <select id="time-format" name="time_format">
          <option value="${TIME_FORMATS.HMMSS}" ${timeFormat === TIME_FORMATS.HMMSS ? 'selected' : ''}>HH:MM:SS</option>
          <option value="${TIME_FORMATS.DECIMAL}" ${timeFormat === TIME_FORMATS.DECIMAL ? 'selected' : ''}>Decimal</option>
        </select>
      </div>
      <button type="submit" class="btn btn--primary">Update Details</button>
      <span id="details-msg" class="form-msg"></span>
    </form>
  `;
  container.appendChild(detailsSection);

  detailsSection.querySelector('#details-form').addEventListener('submit', async (e) => {
    e.preventDefault();
    const msgEl = detailsSection.querySelector('#details-msg');
    const name = detailsSection.querySelector('#profile-name').value.trim();
    const email = detailsSection.querySelector('#profile-email').value.trim();
    const tf = detailsSection.querySelector('#time-format').value;

    const validation = validateProfileUpdate({ name, email, time_format: tf });
    if (!validation.valid) {
      msgEl.textContent = validation.errors.join(', ');
      msgEl.className = 'form-msg form-msg--error';
      return;
    }

    try {
      await updatePilot(pilotId, { name, email, meta: { ...pilot.meta, time_format: tf } });
      msgEl.textContent = 'Details updated.';
      msgEl.className = 'form-msg form-msg--success';
    } catch (err) {
      msgEl.textContent = err.message || 'Update failed.';
      msgEl.className = 'form-msg form-msg--error';
    }
  });

  // ── Password section ──────────────────────────────────────────────────
  const pwSection = document.createElement('section');
  pwSection.className = 'profile-section';
  pwSection.innerHTML = `
    <h2>Password</h2>
    <p class="profile-password-status">Password is set.</p>
    <form id="password-form" class="profile-form">
      <div class="form-group">
        <label for="current-password">Current Password</label>
        <input type="password" id="current-password" name="currentPassword" required />
      </div>
      <div class="form-group">
        <label for="new-password">New Password</label>
        <input type="password" id="new-password" name="newPassword" minlength="8" required />
      </div>
      <button type="submit" class="btn btn--primary">Change Password</button>
      <span id="password-msg" class="form-msg"></span>
    </form>
  `;
  container.appendChild(pwSection);

  pwSection.querySelector('#password-form').addEventListener('submit', async (e) => {
    e.preventDefault();
    const msgEl = pwSection.querySelector('#password-msg');
    const currentPassword = pwSection.querySelector('#current-password').value;
    const newPassword = pwSection.querySelector('#new-password').value;

    const validation = validatePasswordChange({ currentPassword, newPassword });
    if (!validation.valid) {
      msgEl.textContent = validation.errors.join(', ');
      msgEl.className = 'form-msg form-msg--error';
      return;
    }

    try {
      await getSupabase().auth.updateUser({ password: newPassword });
      msgEl.textContent = 'Password changed.';
      msgEl.className = 'form-msg form-msg--success';
      pwSection.querySelector('#current-password').value = '';
      pwSection.querySelector('#new-password').value = '';
    } catch (err) {
      msgEl.textContent = err.message || 'Password change failed.';
      msgEl.className = 'form-msg form-msg--error';
    }
  });
}

// ---------------------------------------------------------------------------
// Licence helpers (DOM)
// ---------------------------------------------------------------------------

function renderLicenceList(licences, pilotId) {
  const listEl = document.getElementById('licence-list');
  if (!listEl) return;
  listEl.innerHTML = '';

  if (!licences || licences.length === 0) {
    listEl.innerHTML = '<p class="empty-state">No licences added yet.</p>';
    return;
  }

  for (const lic of licences) {
    const card = createLicenceCard(lic, {
      onEdit: (l) => openLicenceModal(l, pilotId),
      onDelete: (l) => handleDeleteLicence(l, pilotId),
    });
    listEl.appendChild(card);
  }
}

function openLicenceModal(licence, pilotId) {
  const isEdit = !!licence;
  openDetailModal({
    title: isEdit ? 'Edit Licence' : 'Add Licence',
    fields: [
      { name: 'licence_type', label: 'Licence Type', type: 'text', required: true },
      { name: 'rating_category', label: 'Rating Category', type: 'text', required: true },
      { name: 'expiry_date', label: 'Expiry Date', type: 'date', required: true },
    ],
    values: isEdit ? {
      licence_type: licence.licence_type,
      rating_category: licence.rating_category,
      expiry_date: licence.expiry_date,
    } : null,
    onSave: async (formData) => {
      const payload = prepareLicencePayload(formData);
      if (isEdit) {
        await updatePilotLicence(licence.id, payload);
      } else {
        await createPilotLicence({ ...payload, pilot_id: pilotId });
      }
      // Refresh licence list
      const updated = await getPilotLicences(pilotId);
      renderLicenceList(updated, pilotId);
    },
    onDelete: isEdit ? async () => {
      await deletePilotLicence(licence.id);
      const updated = await getPilotLicences(pilotId);
      renderLicenceList(updated, pilotId);
    } : null,
  });
}

async function handleDeleteLicence(licence, pilotId) {
  if (!confirm(`Delete licence "${licence.licence_type}"?`)) return;
  try {
    await deletePilotLicence(licence.id);
    const updated = await getPilotLicences(pilotId);
    renderLicenceList(updated, pilotId);
  } catch (err) {
    alert('Failed to delete licence: ' + (err.message || 'Unknown error'));
  }
}
